/**
 * Query keys — the one home for the cache-key shape of every generated query.
 *
 * The builders key their queries with these functions, and the generated
 * `queryKeys.<operationId>(params?)` helpers (generated.ts) call the same
 * functions, so a caller refreshing a query can never hand-write a key that
 * silently stops matching:
 *
 *   list route:   [resource, "list", path]            + [params]           (list params)
 *   other GET:    [resource, "custom", path]          + [params[, query]]  (path params, query)
 *
 * A shorter key is a prefix: TanStack Query matches `[resource, "custom", path]`
 * against every params/query combination of that route, and a partial `params`
 * object against every query that carries those values.
 */

/** The path segment of a key: the route template without leading slashes. */
const keyPath = (path: string): string => path.replace(/^\/+/, "");

/**
 * Key (or prefix) of a list route's queries.
 *
 * @param resource - Resource name, e.g. "orders"
 * @param path - Route template, e.g. "/list"
 * @param params - The list params (filters, sort, paging); omit for the prefix
 */
export function listQueryKey(
  resource: string,
  path: string,
  params?: unknown,
): readonly unknown[] {
  const key: unknown[] = [resource, "list", keyPath(path)];
  if (params !== undefined) key.push(params);
  return key;
}

/**
 * Key (or prefix) of a non-list GET route's queries.
 *
 * Trailing slots are omitted when undefined so the key stays compact; when a
 * query is set without params, an explicit `undefined` keeps the query in slot 5.
 *
 * @param resource - Resource name, e.g. "orders"
 * @param path - Route template, e.g. "/:orderId"
 * @param params - Path params (may be partial for a prefix match)
 * @param query - Query-string values
 */
export function customQueryKey(
  resource: string,
  path: string,
  params?: unknown,
  query?: unknown,
): readonly unknown[] {
  const key: unknown[] = [resource, "custom", keyPath(path)];
  if (params !== undefined || query !== undefined) key.push(params);
  if (query !== undefined) key.push(query);
  return key;
}
